export type SelectableOption = {
  id: string;
  name: string;
};

/**
 * MUI hands these helpers the option it is acting on, and that option can be
 * missing: on Enter, `useAutocomplete` passes
 * `filteredOptions[highlightedIndexRef.current]` to `selectNewValue` guarded
 * only by `!== -1`, so a highlighted index left pointing past the end of a
 * narrowed option list reads off the end of the array (SC-4024). MUI's default
 * comparator is `(a, b) => a === b`, which tolerates that, so MUI has no reason
 * to guard the call. Ours dereferences the option, so it has to guard itself.
 */
export const getOptionId = <T extends SelectableOption | string>(
  option: T | undefined,
): string | undefined => (typeof option === 'string' ? option : option?.id);

/** A missing option matches nothing, not even another missing option. */
export const isSameOption = <T extends SelectableOption | string>(
  opt: T | undefined,
  val: T | undefined,
): boolean => {
  const optId = getOptionId(opt);
  return optId !== undefined && optId === getOptionId(val);
};

/**
 * MUI appends the option it was given to the value array even when that option
 * is missing, so drop nullish entries before anything downstream - including
 * the consumer's `onChange` - dereferences them.
 */
export const withoutMissingOptions = <T>(
  value: (T | undefined | null)[],
): T[] => value.filter((v): v is T => v != null);
